interface FloorplanBeds {
  floorplan_beds_min?: string | number | null;
}

interface BedroomPropertyFields {
  property_bedrooms_min?: string | number | null;
  property_bedrooms_max?: string | number | null;
  property_bedrooms_range?: string | null;
  floorplans?: FloorplanBeds[] | null;
}

function parseBedCount(raw: string | number | null | undefined): number | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === 'studio' || trimmed === 'std') return 0;
  const num = parseInt(trimmed, 10);
  if (!isNaN(num) && num >= 0 && num <= 10) return num;
  return null;
}

export function bedroomCountsFromFloorplans(floorplans: FloorplanBeds[] | null | undefined): number[] {
  if (!floorplans?.length) return [];
  const beds = new Set<number>();
  for (const plan of floorplans) {
    const count = parseBedCount(plan.floorplan_beds_min);
    if (count != null) beds.add(count);
  }
  return [...beds].sort((a, b) => a - b);
}

export function bedroomCountsFromMinMax(
  min: string | number | null | undefined,
  max: string | number | null | undefined,
): number[] {
  const minN = parseBedCount(min);
  const maxN = parseBedCount(max);
  if (minN == null || maxN == null || maxN < minN) return [];
  return Array.from({ length: maxN - minN + 1 }, (_, i) => minN + i);
}

export function bedroomCountsFromRangeText(rangeText: string | null | undefined): number[] {
  if (!rangeText || typeof rangeText !== 'string') return [];

  const cleaned = rangeText
    .replace(/\b(bedrooms?|beds?|br)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return [];

  const single = cleaned.match(/^(\d+)$/);
  if (single) {
    const n = parseInt(single[1], 10);
    return n >= 0 && n <= 10 ? [n] : [];
  }

  const range = cleaned.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (range) {
    const start = parseInt(range[1], 10);
    const end = parseInt(range[2], 10);
    if (start >= 0 && end >= start && end <= 10) {
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  }

  const list = cleaned
    .split(/,\s*/)
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => !isNaN(n) && n >= 0 && n <= 10);
  if (list.length) return [...new Set(list)].sort((a, b) => a - b);

  return [];
}

export function resolveBedroomCounts(property: BedroomPropertyFields | null | undefined): number[] {
  const fromFloorplans = bedroomCountsFromFloorplans(property?.floorplans);
  if (fromFloorplans.length) return fromFloorplans;

  const fromMinMax = bedroomCountsFromMinMax(
    property?.property_bedrooms_min,
    property?.property_bedrooms_max,
  );
  if (fromMinMax.length) return fromMinMax;

  return bedroomCountsFromRangeText(property?.property_bedrooms_range);
}

export function formatBedroomCount(count: number): string {
  return count === 0 ? 'Studio' : String(count);
}

/** e.g. "1, 2, & 3" or "Studio, 1, & 2" — no bedroom/bed wording. */
export function formatBedroomList(counts: number[]): string {
  const labels = counts.map(formatBedroomCount);
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, & ${labels[labels.length - 1]}`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * After richtext field-token resolution, swap the bedrooms_range token value
 * for an individual count list (e.g. "1, 2, & 3") without bed/bedroom words.
 * Surrounding subtitle copy is left unchanged.
 */
export function transformBedroomRangeInResolvedHtml(
  html: string,
  property: BedroomPropertyFields | null | undefined,
): string {
  if (!html || !property) return html;

  const formatted = formatBedroomList(resolveBedroomCounts(property));
  if (!formatted) return html;

  const replacements = new Set<string>();
  const raw = property.property_bedrooms_range;
  if (raw != null && String(raw).trim()) {
    replacements.add(String(raw).trim());
  }

  let result = html;
  const formattedEscaped = escapeHtml(formatted);

  for (const text of replacements) {
    const escaped = escapeHtml(text);
    if (result.includes(escaped)) {
      result = result.split(escaped).join(formattedEscaped);
      continue;
    }
    const pattern = new RegExp(escapeRegex(escaped), 'gi');
    result = result.replace(pattern, formattedEscaped);
  }

  return result;
}
